import {hpcAttrs} from '@github-ui/hpc'
import {use, useMemo} from 'react'

import {Hero, Grid, Text} from '@primer/react-brand/esm'

import {
  PLAN_TYPES,
  PlanTypeSegmentedControl,
} from '../../../../brand/components/PlanTypeSegmentedControl/PlanTypeSegmentedControl'
import type {GenericGroup, GenericContent, GenericSectionWithIds} from '../../../../brand/lib/types/contentful'

import {PlanTypeContext} from '../_context/PlanTypeContext'

import {PricingPlans} from '../_components/PricingPlans/PricingPlans'
import IdeList from '../_components/IdeList/IdeList'

type Props = {
  contentfulContent: GenericSectionWithIds
}

export function HeroSection(props: Props) {
  const {contentfulContent} = props

  const {
    featuresCopilotPlansHero,
    featuresCopilotPlansHeroIdeList,
    featuresCopilotPlanTypes,
    featuresCopilotPricingOptions,
  } = contentfulContent.ids as {
    featuresCopilotPlansHero: GenericContent
    featuresCopilotPlansHeroIdeList: GenericContent
    featuresCopilotPlanTypes: GenericContent
    featuresCopilotPricingOptions: GenericGroup
  }

  const {planType, setPlanType} = use(PlanTypeContext)

  const planItems = useMemo(
    () =>
      featuresCopilotPlanTypes?.fields.content?.map(item => ({
        id: `${item.fields.id}-plan-tab`,
        value: item.fields.id,
        label: item.fields.heading,
      })) || [
        {
          id: 'individual-plan-tab',
          value: PLAN_TYPES.Individual,
          label: 'For individuals',
        },
        {
          id: 'business-plan-tab',
          value: PLAN_TYPES.Business,
          label: 'For businesses',
        },
      ],
    [featuresCopilotPlanTypes?.fields.content],
  )

  return (
    <Grid as="section" className="position-relative">
      <Grid.Column span={12}>
        {featuresCopilotPlansHero ? (
          <Hero align="center" className="tmp-pb-4">
            <Hero.Heading size="2" {...hpcAttrs()}>
              {featuresCopilotPlansHero.fields.heading}
            </Hero.Heading>
          </Hero>
        ) : null}

        {featuresCopilotPlanTypes ? (
          <div className="d-flex flex-justify-center px-2 tmp-mb-7">
            <h2 className="visually-hidden" id="pricing-plans">
              {featuresCopilotPlanTypes.fields.heading || 'Pricing plans'}
            </h2>

            <PlanTypeSegmentedControl
              value={planType}
              onChange={setPlanType}
              items={planItems}
              heading={featuresCopilotPlanTypes.fields.heading || 'Pricing plans'}
            />
          </div>
        ) : null}

        {featuresCopilotPricingOptions ? <PricingPlans contentfulContent={featuresCopilotPricingOptions} /> : null}

        <div className="tmp-mb-5">
          {featuresCopilotPlansHeroIdeList ? (
            <Text as="p" size="200" variant="muted" weight="normal" align="center" className="tmp-mb-5">
              {featuresCopilotPlansHeroIdeList.fields.heading}
            </Text>
          ) : null}

          <IdeList type="small" location="pricing-cards" />
        </div>
      </Grid.Column>
    </Grid>
  )
}
